// src/pages/ProductDetails.jsx - Professional Enhanced Design with COMPLETE SEO
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, Tag, Package, Info, CreditCard, Minus, Plus, CheckCircle2, Star } from 'lucide-react';
import { productsAPI, categoriesAPI, cartAPI, seoAPI } from '../services/api';
import ProductCard from '../components/ProductCard';
import ReviewSection from '../components/ReviewSection';
import SmartSearch from '../components/SmartSearch';
import SEO from '../components/SEO';
import { track, trackOnce } from '../utils/metaPixel';
import './css/ProductDetails.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
const API_ORIGIN = API_BASE_URL.replace(/\/api\/?$/, '');
const FALLBACK_IMAGE = '/placeholder-image.jpg';

const normalizeImageUrl = (url) => {
  if (!url || typeof url !== 'string') return '';
  if (url.startsWith('http') || url.startsWith('data:')) return url;
  if (url.startsWith('//')) return `${window.location.protocol}${url}`;
  const cleaned = url.replace(/^\/+/, '');
  if (cleaned.startsWith('images/') || cleaned.startsWith('uploads/') || cleaned.startsWith('products/')) {
    return `${API_ORIGIN}/${cleaned}`;
  }
  return `${API_ORIGIN}/images/${cleaned}`;
};

const parseGalleryImages = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value !== 'string') return [];
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return parsed;
    if (typeof parsed === 'string') {
      try {
        const nested = JSON.parse(parsed);
        return Array.isArray(nested) ? nested : [parsed];
      } catch (nestedErr) {
        return [parsed];
      }
    }
    return [value];
  } catch (parseErr) {
    return [value];
  }
};

const ProductDetails = ({ openCart }) => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [product, setProduct] = useState(null);
  const [seoData, setSeoData] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [categorySlug, setCategorySlug] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [mainImage, setMainImage] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [showZoom, setShowZoom] = useState(false);
  const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 });
  const [lensPosition, setLensPosition] = useState({ x: 0, y: 0 });
  const [addingToCart, setAddingToCart] = useState(false);

  const imageWrapperRef = useRef(null);
  const activeRequestIdRef = useRef(0);

  useEffect(() => {
    const requestId = ++activeRequestIdRef.current;
    setSeoData(null);
    fetchProductAndRelated(requestId);
    fetchSEO(requestId);

    return () => {
      activeRequestIdRef.current++;
    };
  }, [slug]);

  useEffect(() => {
    if (!product?.id) return;
    const value = Number(product.salePrice ?? product.regularPrice ?? 0);
    trackOnce(
      'ViewContent',
      {
        content_ids: [String(product.id)],
        content_name: product.name,
        content_type: 'product',
        value,
        currency: 'TND'
      },
      `viewcontent:${location.key}:${product.id}`
    );
  }, [product?.id, product?.name, product?.salePrice, product?.regularPrice, location.key]);

  const fetchSEO = async (requestId) => {
    try {
      const { data } = await seoAPI.getProductMetaBySlug(slug);
      if (requestId !== activeRequestIdRef.current) return;
      setSeoData(data);
    } catch (err) {
      console.error('Failed to fetch SEO data:', err);
    }
  };

  const fetchProductAndRelated = async (requestId) => {
    setLoading(true);
    setError('');
    try {
      const { data } = await productsAPI.getBySlug(slug);
      if (requestId !== activeRequestIdRef.current) return;

      const normalizedGallery = parseGalleryImages(data.galleryImages)
        .map(normalizeImageUrl)
        .filter(Boolean);
      const normalizedCover = normalizeImageUrl(data.imageUrl);
      setProduct(data);
      setMainImage(normalizedCover || normalizedGallery[0] || '');
      setQuantity(1);
      setSelectedOptions({});
      setCategorySlug('');
      
      try {
        if (data.categoryId) {
          const { data: categoryData } = await categoriesAPI.getById(data.categoryId);
          if (requestId !== activeRequestIdRef.current) return;
          setCategorySlug(categoryData?.slug || '');
        }
      } catch (categoryErr) {
        if (requestId !== activeRequestIdRef.current) return;
        console.error('Failed to load category details:', categoryErr);
        setCategorySlug('');
      }
      
      try {
        const { data: relatedData } = await productsAPI.getAll(data.categoryId || null);
        if (requestId !== activeRequestIdRef.current) return;
        const currentProductId = data.id;
        const filteredRelated = relatedData
          .filter(p => p.id !== currentProductId)
          .slice(0, 4);
        setRelatedProducts(filteredRelated);
      } catch (relatedErr) {
        if (requestId !== activeRequestIdRef.current) return;
        console.error('Failed to load related products:', relatedErr);
        setRelatedProducts([]);
      }
      
      if (requestId !== activeRequestIdRef.current) return;
      setLoading(false);
    } catch (err) {
      if (requestId !== activeRequestIdRef.current) return;
      console.error('Failed to load product:', err);
      setError('Impossible de charger les details du produit');
      setLoading(false);
    }
  };

  const handleProductUpdate = (updatedProduct) => {
    setProduct(prev => ({
      ...prev,
      averageRating: updatedProduct.averageRating,
      totalReviews: updatedProduct.totalReviews
    }));
  };

  const getPrice = (price) => Number(price || 0).toFixed(2);

  const getSelectedOptionsExtra = (targetProduct, selections) => {
    if (!targetProduct?.options || !selections) return 0;
    return targetProduct.options.reduce((sum, group) => {
      const selectedValueId = selections[group.id];
      if (!selectedValueId) return sum;
      const selectedValue = group.values?.find(v => v.id === Number(selectedValueId));
      const extra = Number(selectedValue?.extra_price || 0);
      return sum + extra;
    }, 0);
  };

  const getItemValue = (targetProduct, selections) => {
    const basePrice = Number(targetProduct?.salePrice ?? targetProduct?.regularPrice ?? 0);
    const extras = getSelectedOptionsExtra(targetProduct, selections);
    return basePrice + extras;
  };

  const handleQuantityChange = (newQty) => {
    if (newQty < 1) return;
    if (product.stock && newQty > product.stock) {
      alert(`Il ne reste que ${product.stock} en stock.`);
      return;
    }
    setQuantity(newQty);
  };

  const isOptionsComplete = () => {
    if (!product.options || product.options.length === 0) return true;
    const requiredOptions = product.options.filter(opt => !!opt.is_required);
    if (requiredOptions.length === 0) return true;
    const missingRequired = requiredOptions.filter(opt => !selectedOptions[opt.id]);
    return missingRequired.length === 0;
  };

  const getMissingRequiredMessage = () => {
    if (!product.options || product.options.length === 0) return '';
    const requiredOptions = product.options.filter(opt => !!opt.is_required);
    const missing = requiredOptions.filter(opt => !selectedOptions[opt.id]).map(opt => opt.name);
    return missing.length > 0 ? `Veuillez selectionner : ${missing.join(', ')}` : '';
  };

  const handleAddToCart = async () => {
    if (!product || addingToCart) return;
    if (!isOptionsComplete()) {
      alert(getMissingRequiredMessage());
      return;
    }
    try {
      setAddingToCart(true);
      await cartAPI.addItem({ 
        productId: product.id, 
        quantity, 
        selected_options: selectedOptions 
      });
      track('AddToCart', {
        content_ids: [String(product.id)],
        content_name: product.name,
        content_type: 'product',
        value: getItemValue(product, selectedOptions),
        currency: 'TND'
      });
      openCart();
    } catch (err) {
      console.error('Failed to add to cart:', err);
      alert(err.response?.data?.message || "Impossible d'ajouter au panier");
    } finally {
      setAddingToCart(false);
    }
  };

  const handlePlaceOrder = async () => {
    if (!product || addingToCart) return;
    if (!isOptionsComplete()) {
      alert(getMissingRequiredMessage());
      return;
    }
    try {
      setAddingToCart(true);
      await cartAPI.addItem({ 
        productId: product.id, 
        quantity, 
        selected_options: selectedOptions 
      });
      track('AddToCart', {
        content_ids: [String(product.id)],
        content_name: product.name,
        content_type: 'product',
        value: getItemValue(product, selectedOptions),
        currency: 'TND'
      });
      navigate('/checkout');
    } catch (err) {
      console.error('Failed to add to cart:', err);
      alert(err.response?.data?.message || "Impossible d'ajouter au panier");
    } finally {
      setAddingToCart(false);
    }
  };

  const handleZoom = (clientX, clientY) => {
    const wrapper = imageWrapperRef.current;
    if (!wrapper) return;

    const rect = wrapper.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;

    const clampedX = Math.max(0, Math.min(x, rect.width));
    const clampedY = Math.max(0, Math.min(y, rect.height));

    const xPercent = (clampedX / rect.width) * 100;
    const yPercent = (clampedY / rect.height) * 100;

    setLensPosition({ x: clampedX, y: clampedY });
    setZoomPosition({ x: xPercent, y: yPercent });
  };

  const handleMouseMove = (e) => handleZoom(e.clientX, e.clientY);
  const handleMouseEnter = () => setShowZoom(true);
  const handleMouseLeave = () => setShowZoom(false);

  const handleTouchStart = (e) => {
    e.preventDefault();
    const touch = e.touches[0];
    handleZoom(touch.clientX, touch.clientY);
    setShowZoom(true);
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
    const touch = e.touches[0];
    handleZoom(touch.clientX, touch.clientY);
  };

  const handleTouchEnd = () => setShowZoom(false);

  useEffect(() => {
    const wrapper = imageWrapperRef.current;
    if (!wrapper) return;

    wrapper.addEventListener('touchstart', handleTouchStart, { passive: false });
    wrapper.addEventListener('touchmove', handleTouchMove, { passive: false });
    wrapper.addEventListener('touchend', handleTouchEnd);

    return () => {
      wrapper.removeEventListener('touchstart', handleTouchStart);
      wrapper.removeEventListener('touchmove', handleTouchMove);
      wrapper.removeEventListener('touchend', handleTouchEnd);
    };
  }, [mainImage]);

  if (loading) {
    return (
      <div className="pd-loading-container">
        <div className="pd-spinner"></div>
        <p>Chargement du produit...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="pd-error-container">
        <p>{error}</p>
        <button onClick={() => navigate('/')} className="pd-back-btn">
          Retour aux produits
        </button>
      </div>
    );
  }
  
  if (!product) {
    return (
      <div className="pd-error-container">
        <p>Produit introuvable</p>
        <button onClick={() => navigate('/')} className="pd-back-btn">
          Retour aux produits
        </button>
      </div>
    );
  }

  const isOutOfStock = product.stock <= 0;
  const isAddToCartDisabled = isOutOfStock || quantity === 0 || !isOptionsComplete() || addingToCart;
  
  const galleryImages = parseGalleryImages(product.galleryImages);
  const normalizedGalleryImages = galleryImages.map(normalizeImageUrl).filter(Boolean);
  const coverImage = normalizeImageUrl(product.imageUrl);
  const allGalleryImages = Array.from(new Set([coverImage, ...normalizedGalleryImages].filter(Boolean)));
  const activeImage = mainImage || allGalleryImages[0] || FALLBACK_IMAGE;
	  const hasOptions = product.options && product.options.length > 0;
	  const requiredOptionsCount = hasOptions ? product.options.filter(opt => !!opt.is_required).length : 0;
	  const selectedRequiredCount = hasOptions ? product.options.filter(opt => !!opt.is_required && selectedOptions[opt.id]).length : 0;
	  const categoryUrl = categorySlug ? `/category/${categorySlug}` : '/categories';

  return (
    <>
      {seoData ? (
        <SEO
          title={seoData.title}
          description={seoData.description}
          keywords={seoData.keywords}
          image={seoData.image}
          url={`/product/${product.slug || slug}`}
          type="product"
          product={product}
          structuredData={seoData.structuredData}
	          breadcrumbs={[
	            { name: 'Accueil', url: '/' },
	            { name: product.categoryName || 'Cat\u00E9gorie', url: categoryUrl },
	            { name: product.name, url: `/product/${product.slug || slug}` }
	          ]}
	        />
      ) : (
        <SEO
          title={`${product.name} - Acheter en ligne | Reyza`}
          description={product.shortDescription || `Achetez ${product.name} au meilleur prix`}
          image={product.imageUrl}
          url={`/product/${product.slug || slug}`}
          type="product"
          product={product}
	          breadcrumbs={[
	            { name: 'Accueil', url: '/' },
	            { name: product.categoryName || 'Cat\u00E9gorie', url: categoryUrl },
	            { name: product.name, url: `/product/${product.slug || slug}` }
	          ]}
	        />
      )}

      <div className="pd-container">
        <div className="pd-breadcrumb">
          <button onClick={() => navigate('/')} className="pd-breadcrumb-link">
            <ArrowLeft size={16} />
            Produits
          </button>
          <span className="pd-breadcrumb-separator">/</span>
          <span className="pd-breadcrumb-current">{product.name}</span>
        </div>
        
        <div className="pd-content">
          <div className="pd-gallery-section">
            <div 
              ref={imageWrapperRef}
              className="pd-main-image-container"
              onMouseMove={handleMouseMove}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <img 
                src={activeImage} 
                alt={product.name} 
                className="pd-main-image" 
                loading="eager"
                decoding="async"
                onError={(e) => {
                  if (e.currentTarget.src !== FALLBACK_IMAGE) {
                    e.currentTarget.src = FALLBACK_IMAGE;
                  }
                }}
              />
              
              {showZoom && (
                <>
                  <div 
                    className="pd-zoom-lens"
                    style={{
                      left: `${lensPosition.x}px`,
                      top: `${lensPosition.y}px`,
                      backgroundImage: `url(${activeImage})`,
                      backgroundPosition: `${zoomPosition.x}% ${zoomPosition.y}%`,
                    }}
                  />
                  <div 
                    className="pd-zoomed-preview"
                    style={{
                      backgroundImage: `url(${activeImage})`,
                      backgroundPosition: `${zoomPosition.x}% ${zoomPosition.y}%`,
                    }}
                  />
                </>
              )}
            </div>

            {allGalleryImages.length > 1 && (
              <div className="pd-thumbnails">
                {allGalleryImages.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setMainImage(img)}
                    className={`pd-thumbnail ${img === mainImage ? 'pd-thumbnail-active' : ''}`}
                  >
                    <img
                      src={img}
                      alt={`Vue ${idx + 1}`}
                      loading="lazy"
                      decoding="async"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="pd-info-section">
            <div className="pd-header">
              <h1 className="pd-title">{product.name}</h1>
              
              {product.averageRating && Number(product.averageRating) > 0 && (
                <div className="pd-rating">
                  <div className="pd-stars">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        size={16}
                        className={i < Math.round(Number(product.averageRating)) ? 'pd-star-filled' : 'pd-star-empty'}
                        fill={i < Math.round(Number(product.averageRating)) ? 'currentColor' : 'none'}
                      />
                    ))}
                  </div>
                  <span className="pd-rating-text">
                    {Number(product.averageRating).toFixed(1)} ({product.totalReviews || 0} avis)
                  </span>
                </div>
              )}
            </div>

            {product.shortDescription && (
              <p className="pd-short-desc">{product.shortDescription}</p>
            )}

            {product.tags && (
              <div className="pd-tags">
                {product.tags.split(',').map((tag, idx) => (
                  <span key={idx} className="pd-tag">
                    <Tag size={12} />
                    {tag.trim()}
                  </span>
                ))}
              </div>
            )}
            
            <div className="pd-price-box">
              <div className="pd-price-main">
                <span className="pd-price">DT {getPrice(product.salePrice || product.regularPrice)}</span>
                {product.salePrice && (
                  <>
                    <span className="pd-price-original">DT {getPrice(product.regularPrice)}</span>
                    <span className="pd-discount-badge">
                      Economisez {Math.round(((product.regularPrice - product.salePrice) / product.regularPrice) * 100)}%
                    </span>
                  </>
                )}
              </div>
              <div className="pd-stock-badge">
                <Package size={16} />
                {isOutOfStock ? (
                  <span className="pd-stock-out">Rupture de stock</span>
                ) : (
                  <span className="pd-stock-in">{product.stock} en stock</span>
                )}
              </div>
            </div>

            {hasOptions && (
              <div className="pd-options">
                <div className="pd-options-header">
                  <h3>Personnalisez votre commande</h3>
                  {requiredOptionsCount > 0 && (
                    <div className="pd-options-progress">
                      <span>{selectedRequiredCount}/{requiredOptionsCount} requis</span>
                      <div className="pd-progress-bar">
                        <div 
                          className="pd-progress-fill"
                          style={{ width: `${(selectedRequiredCount / requiredOptionsCount) * 100}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="pd-options-list">
                  {product.options.map((option) => {
                    const isRequired = !!option.is_required;
                    const isSelected = !!selectedOptions[option.id];
                    
                    return (
                      <div key={option.id} className={`pd-option ${isSelected ? 'pd-option-selected' : ''}`}>
                        <div className="pd-option-header">
                          <label className="pd-option-label">{option.name}</label>
                          <span className={`pd-option-badge ${isRequired ? 'pd-badge-required' : 'pd-badge-optional'}`}>
                            {isRequired ? 'Requis' : 'Optionnel'}
                          </span>
                          {isSelected && <CheckCircle2 className="pd-check-icon" size={18} />}
                        </div>

                        <div className="pd-option-values">
                          {option.values.map((value) => {
                            const isValueSelected = selectedOptions[option.id] === value.id;
                            
                            return (
                              <button
                                key={value.id}
                                onClick={() => setSelectedOptions(prev => ({ ...prev, [option.id]: value.id }))}
                                className={`pd-value-btn ${isValueSelected ? 'pd-value-selected' : ''}`}
                              >
                                <span className="pd-value-text">{value.value}</span>
                                {value.extra_price > 0 && (
                                  <span className="pd-value-price">+DT {getPrice(value.extra_price)}</span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {!isOptionsComplete() && (
                  <div className="pd-alert">
                    <Info size={16} />
                    <span>{getMissingRequiredMessage()}</span>
                  </div>
                )}
              </div>
            )}

            <div className="pd-quantity">
              <label>Quantite</label>
              <div className="pd-quantity-control">
	                <button
	                  onClick={() => handleQuantityChange(quantity - 1)}
	                  disabled={quantity <= 1 || isOutOfStock}
	                  className="pd-qty-btn"
	                  aria-label="Diminuer la quantite"
	                >
	                  <Minus className="pd-qty-icon" />
	                </button>
                <input 
                  type="text" 
                  value={quantity} 
                  readOnly 
                  className="pd-qty-input"
                />
	                <button
	                  onClick={() => handleQuantityChange(quantity + 1)}
	                  disabled={(product.stock && quantity >= product.stock) || isOutOfStock}
	                  className="pd-qty-btn"
	                  aria-label="Augmenter la quantite"
	                >
	                  <Plus className="pd-qty-icon" />
	                </button>
              </div>
            </div>

            <div className="pd-actions">
              <button
                onClick={handleAddToCart}
                disabled={isAddToCartDisabled}
                className="pd-btn pd-btn-cart"
              >
                <ShoppingCart size={20} />
                {addingToCart ? 'Ajout...' : isOutOfStock ? 'Rupture de stock' : 'Ajouter au panier'}
              </button>
              <button
                onClick={handlePlaceOrder}
                disabled={isAddToCartDisabled}
                className="pd-btn pd-btn-buy"
              >
                <CreditCard size={20} />
                {addingToCart ? 'Traitement...' : isOutOfStock ? 'Indisponible' : 'Acheter maintenant'}
              </button>
            </div>

            <SmartSearch product={product} />
          </div>
        </div>

        {product.longDescription && (
          <div className="pd-description-section">
            <h2>
              <Info size={20} />
              Details du produit
            </h2>
            <p>{product.longDescription}</p>
          </div>
        )}

        {relatedProducts.length > 0 && (
          <div className="pd-related-section">
            <h2>Vous aimerez aussi</h2>
            <div className="pd-related-grid">
              {relatedProducts.map((relatedProduct) => (
                <ProductCard
                  key={relatedProduct.id}
                  product={relatedProduct}
                  openCart={openCart}
                />
              ))}
            </div>
          </div>
        )}

        <ReviewSection 
          product={product} 
          onProductUpdate={handleProductUpdate}
        />
      </div>
    </>
  );
};

export default ProductDetails;
